/**
 * La pantalla de entrada.
 *
 * ===========================================================================
 * ES LA PUERTA, Y SE VE ANTES QUE NADA
 *
 * Todo lo demas de esta interfaz lo ve alguien que ya trabaja aqui. Esto lo ve
 * cualquiera que abra la URL, incluido quien la abre por primera vez. De ahi
 * las tres decisiones de esta pantalla:
 *
 *   1. ES BILINGUE ANTES DE ENTRAR. El selector de idioma esta en el propio
 *      login y no despues. La API no puede saber que idioma prefiere quien
 *      todavia no ha entrado --por eso el error de credenciales viaja en los
 *      dos idiomas, ver services/api/src/sesion.ts-- pero la pantalla si:
 *      recuerda el ultimo elegido, igual que dentro.
 *
 *   2. EL ERROR NO DICE SI EL CORREO EXISTE. Se pinta el mensaje que mande la
 *      API, que es uno solo para las cuatro formas de fallar. Esta pantalla no
 *      añade "revisa tu contrasena" ni "ese correo no esta registrado": eso
 *      convertiria el formulario en un comprobador de quien trabaja aqui.
 *
 *   3. NO HAY "OLVIDE MI CONTRASENA". No es un olvido: no hay envio de correo
 *      todavia, y un enlace que no lleva a ningun sitio es peor que su
 *      ausencia. Lo que si hay es la frase que dice a quien pedirselo.
 *
 * ===========================================================================
 * LA IMAGEN NO PUEDE RETRASAR EL FORMULARIO
 *
 * El fondo es una fotografia de 27 kB en el peor caso razonable, pero lo que
 * importa no es el peso: es el orden. El formulario se pinta con el degradado
 * de la paleta debajo y la foto llega cuando llegue --`<img>` nunca bloquea el
 * primer pintado, y el panel tiene fondo propio--. Si la red va mal, se entra
 * igual sobre un fondo azul.
 *
 * `<picture>` con WebP y respaldo JPEG, tres anchos y `sizes`: el navegador
 * pide UNA, la que le sirve. Ver scripts/optimizar-hero.mjs para por que esas
 * variantes existen y por que se versionan en vez de generarse en el build.
 *
 * `alt=""` porque es decorativa: no lleva informacion que el formulario no
 * tenga ya, y un lector de pantalla que la describiera solo añadiria ruido
 * antes del campo de correo.
 *
 * ===========================================================================
 * EN MOVIL LA FOTO NO ENTIERRA EL LOGIN
 *
 * La composicion es un split: panel a la izquierda, foto a la derecha. Por
 * debajo de 900 px se apila, y ahi la foto pasa a ser una BANDA CORTA arriba
 * --alto acotado, no proporcional-- con la marca encima. Una foto a pantalla
 * completa en un movil deja el formulario debajo del pliegue, o sea una puerta
 * a la que hay que hacer scroll para llamar. El detalle esta en login.css.
 */

import { el } from '../dom';
import type { ErrorApi } from '../api';
import { IDIOMAS, nombreIdioma, type Idioma, type Traductor } from '../i18n';
import { simboloMarca } from './marca';

/** Los anchos que existen en `frontend/estatico/`. Ver optimizar-hero.mjs. */
const ANCHOS = [640, 1024, 1600];

/**
 * `sizes` describe QUE HUECO ocupa la imagen, no que archivo se quiere. Con el
 * split, el hueco es el 55% del ancho en pantalla grande y todo el ancho cuando
 * se apila. Sin esto el navegador supone `100vw` y se baja la variante grande
 * para pintarla a media pantalla.
 */
const SIZES = '(max-width: 900px) 100vw, 55vw';

function srcset(extension: string): string {
  return ANCHOS.map((a) => `/login-hero-${String(a)}.${extension} ${String(a)}w`).join(
    ', ',
  );
}

function fondo(): HTMLElement {
  const img = el('img', {
    clase: 'login-hero-img',
    attrs: {
      src: '/login-hero-1024.jpg',
      srcset: srcset('jpg'),
      sizes: SIZES,
      alt: '',
      // Decodificar fuera del hilo principal: el formulario no espera a la
      // foto ni siquiera para pintarse la primera vez.
      decoding: 'async',
      fetchpriority: 'high',
    },
  });

  const webp = el('source', {
    attrs: { type: 'image/webp', srcset: srcset('webp'), sizes: SIZES },
  });

  return el('picture', { clase: 'login-hero-picture' }, [webp, img]);
}

export interface ValoresLogin {
  readonly email: string;
  readonly contrasena: string;
}

export interface OpcionesLogin {
  readonly t: Traductor;
  readonly idioma: Idioma;
  readonly valores: ValoresLogin;
  readonly entrando: boolean;
  readonly error: ErrorApi | null;
  readonly onEntrar: (v: ValoresLogin) => void;
  readonly onIdioma: (i: Idioma) => void;
}

export function pintarLogin(o: OpcionesLogin): HTMLElement {
  const { t } = o;

  const email = el('input', {
    clase: 'control',
    attrs: {
      id: 'login-email',
      type: 'email',
      // `username` y `current-password`: son los nombres que hacen que un
      // gestor de contrasenas ofrezca la que corresponde. Con nombres
      // inventados el gestor no reconoce el formulario y la gente acaba
      // escribiendo la contrasena a mano todos los dias, que es como se
      // terminan eligiendo contrasenas cortas.
      autocomplete: 'username',
      // El correo de la empresa no lleva mayusculas ni el teclado del movil
      // tiene por que proponerlas.
      autocapitalize: 'none',
      autocorrect: 'off',
      spellcheck: 'false',
      required: 'required',
      value: o.valores.email,
    },
  });

  const contrasena = el('input', {
    clase: 'control',
    attrs: {
      id: 'login-contrasena',
      type: 'password',
      autocomplete: 'current-password',
      required: 'required',
      value: o.valores.contrasena,
    },
  });

  const boton = el('button', {
    clase: 'boton boton-primario login-enviar',
    texto: o.entrando ? t('login.entrando') : t('login.entrar'),
    attrs: o.entrando ? { type: 'submit', disabled: 'disabled' } : { type: 'submit' },
  });

  const formulario = el('form', { clase: 'login-form' }, [
    el('div', { clase: 'campo' }, [
      el('label', { texto: t('login.email'), attrs: { for: 'login-email' } }),
      email,
    ]),
    el('div', { clase: 'campo' }, [
      el('label', { texto: t('login.contrasena'), attrs: { for: 'login-contrasena' } }),
      contrasena,
    ]),
    // El error va ENTRE el formulario y el boton, no arriba del todo: es donde
    // esta mirando quien acaba de pulsar. `role="alert"` para que un lector de
    // pantalla lo diga sin que haya que ir a buscarlo.
    o.error === null
      ? null
      : el('p', { clase: 'aviso aviso-error login-error', attrs: { role: 'alert' } }, [
          el('strong', {
            texto:
              o.error.codigo === 'SIN_RED'
                ? t('error.red')
                : t(`error.${o.error.codigo}`),
          }),
          // El mensaje de la API tal cual. Viene en los dos idiomas a proposito
          // --aqui todavia no se sabe cual prefiere quien pregunta-- y es uno
          // solo para las cuatro formas de fallar.
          el('span', { texto: o.error.message }),
        ]),
    boton,
  ]);

  formulario.addEventListener('submit', (ev) => {
    ev.preventDefault();
    if (o.entrando) return;
    o.onEntrar({ email: email.value.trim(), contrasena: contrasena.value });
  });

  const selectIdioma = el('select', {
    clase: 'control control-menor',
    attrs: { id: 'login-idioma', 'aria-label': t('app.idioma') },
  });
  for (const i of IDIOMAS) {
    selectIdioma.appendChild(
      el('option', { texto: nombreIdioma(i), attrs: { value: i } }),
    );
  }
  selectIdioma.value = o.idioma;
  selectIdioma.addEventListener('change', () => {
    o.onIdioma(selectIdioma.value as Idioma);
  });

  const marca = el('div', { clase: 'login-marca' }, [
    el('div', { clase: 'logo' }, [simboloMarca()]),
    el('div', { clase: 'marca' }, [
      el('p', { clase: 'marca-suite', texto: t('app.suite') }),
      // El nombre del modulo, en la puerta. Es lo que dice a donde se ha
      // llegado antes de escribir nada.
      el('h1', { texto: t('app.nombre') }),
    ]),
  ]);

  const panel = el('section', { clase: 'login-panel' }, [
    marca,
    el('h2', { clase: 'login-titulo', texto: t('login.titulo') }),
    el('p', { clase: 'tenue login-intro', texto: t('login.intro') }),
    formulario,
    // Sin recuperacion por correo: no hay envio de correo todavia. En vez de un
    // enlace que no lleva a ningun sitio, la frase que dice a quien pedirselo.
    el('p', { clase: 'tenue login-nota', texto: t('login.sinAcceso') }),
    el('div', { clase: 'login-idioma' }, [
      el('label', {
        clase: 'etiqueta-menor',
        texto: t('app.idioma'),
        attrs: { for: 'login-idioma' },
      }),
      selectIdioma,
    ]),
  ]);

  const hero = el('div', { clase: 'login-hero', attrs: { 'aria-hidden': 'true' } }, [
    fondo(),
    // El velo va en un nodo aparte y no como sombra del contenedor: sobre la
    // foto hay texto, y un degradado pintado por encima de la imagen es lo que
    // garantiza el contraste pase lo que pase con la fotografia que haya
    // detras el dia de mañana.
    el('div', { clase: 'login-velo' }),
    el('p', { clase: 'login-lema', texto: t('login.lema') }),
  ]);

  return el('div', { clase: 'login' }, [panel, hero]);
}

/**
 * Donde poner el cursor al pintar.
 *
 * Se llama despues de insertar en el documento --antes, `focus()` no hace nada
 * en un nodo suelto--. Tras un error el cursor va a la contrasena y no al
 * correo: el correo casi nunca es lo que estaba mal, y mandar ahi el cursor
 * obliga a tabular para escribir lo unico que hay que volver a escribir.
 */
export function enfocarLogin(raiz: HTMLElement, huboError: boolean): void {
  const id = huboError ? '#login-contrasena' : '#login-email';
  raiz.querySelector<HTMLInputElement>(id)?.focus?.();
}
